import React, { useState, useEffect } from 'react';
import { colors } from '../../utils/colors';
import type { ExamStudent, ExamSubjectMeta } from './types';

interface Props {
  student: ExamStudent;
  subject: ExamSubjectMeta;
  examName: string;
  onClose: () => void;
  onSave: (studentId: number, subjectId: number, marks: number | null) => void;
}

const MarksEntrySheet: React.FC<Props> = ({ student, subject, examName, onClose, onSave }) => {
  const initial = student.marks[subject.subjectId];
  const [draft, setDraft] = useState<string>(
    initial !== null && initial !== undefined ? String(initial) : '',
  );

  useEffect(() => {
    const initial = student.marks[subject.subjectId];
    setDraft(initial !== null && initial !== undefined ? String(initial) : '');
  }, [student, subject]);

  const trimmed = draft.trim();
  const parsed = trimmed === '' ? null : Number(trimmed);
  const isInvalid = parsed !== null && (isNaN(parsed) || parsed < 0 || parsed > subject.maxMarks);
  const passed = parsed !== null && !isInvalid && parsed >= subject.minMarks;
  const failed = parsed !== null && !isInvalid && parsed < subject.minMarks;
  const pct = parsed !== null && !isInvalid ? Math.round((parsed / subject.maxMarks) * 100) : null;

  const handleSave = () => {
    if (isInvalid) return;
    onSave(student.id, subject.subjectId, parsed);
    onClose();
  };

  return (
    <>
      <style>{`
        .marks-input::placeholder {
          font-size: 16px;
          font-weight: 600;
          color: #9ca3af;
        }
      `}</style>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)',
          zIndex: 200, animation: 'fadeIn 0.2s ease both',
        }}
      />
      <div
        style={{
          position: 'fixed', left: 0, right: 0, bottom: 0,
          background: '#fff', borderRadius: '22px 22px 0 0',
          padding: '0 20px 40px', zIndex: 201,
          animation: 'sheetSlideUp 0.3s cubic-bezier(.32,.72,0,1) both',
          maxHeight: '85vh', overflowY: 'auto',
        }}
      >
        {/* Drag handle */}
        <div
          style={{
            width: 36, height: 4, borderRadius: 2,
            background: colors.border, margin: '14px auto 20px',
          }}
        />

        {/* Student header */}
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 14,
            background: colors.primaryBg,
            borderRadius: 14, padding: '14px 16px', marginBottom: 16,
          }}
        >
          <div
            style={{
              width: 46, height: 46, borderRadius: '50%',
              background: colors.primary,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 15, fontWeight: 800, color: '#fff', flexShrink: 0,
            }}
          >
            {student.rollNo}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 16, fontWeight: 700, color: colors.text,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}
            >
              {student.name}
            </div>
            <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>
              {student.uid} · Roll {student.rollNo}
            </div>
          </div>
        </div>

        {/* Exam + subject meta */}
        <div
          style={{
            background: '#f8fafc', borderRadius: 12,
            padding: '12px 14px', marginBottom: 18,
            display: 'flex', flexDirection: 'column', gap: 4,
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, color: colors.textMuted, letterSpacing: '0.4px' }}>
            {examName.toUpperCase()}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: colors.text }}>
              {subject.subjectName}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <span
                style={{
                  fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                  background: '#fff', color: colors.primary, border: `1px solid ${colors.borderLight}`,
                }}
              >
                MAX {subject.maxMarks}
              </span>
              <span
                style={{
                  fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                  background: '#fff', color: '#166534', border: '1px solid #bbf7d0',
                }}
              >
                MIN {subject.minMarks}
              </span>
            </div>
          </div>
        </div>

        {/* Marks input */}
        <div style={{ marginBottom: 8 }}>
          <div
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
            <label style={{ fontSize: 13, fontWeight: 700, color: colors.text }}>
              Marks obtained
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {pct !== null && (
                <span
                  style={{
                    fontSize: 11, fontWeight: 700, borderRadius: 20, padding: '2px 8px',
                    color: pct >= 75 ? '#16a34a' : pct >= 40 ? '#d97706' : '#dc2626',
                    background: pct >= 75 ? '#f0fdf4' : pct >= 40 ? '#fffbeb' : '#fee2e2',
                  }}
                >
                  {pct}%
                </span>
              )}
              {passed && (
                <span
                  style={{
                    fontSize: 10, fontWeight: 700, borderRadius: 20, padding: '2px 8px',
                    color: '#16a34a', background: '#f0fdf4', border: '1px solid #bbf7d0',
                  }}
                >
                  PASS
                </span>
              )}
              {failed && (
                <span
                  style={{
                    fontSize: 10, fontWeight: 700, borderRadius: 20, padding: '2px 8px',
                    color: '#dc2626', background: '#fee2e2', border: '1px solid #fecaca',
                  }}
                >
                  FAIL
                </span>
              )}
            </div>
          </div>
          <input
            className="marks-input"
            type="number"
            inputMode="numeric"
            min={0}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Enter obtained marks"
            style={{
              width: '100%', padding: '14px 16px', boxSizing: 'border-box',
              border: `1.5px solid ${
                isInvalid ? '#dc2626' : failed ? '#f97316' : trimmed !== '' ? colors.primary : colors.border
              }`,
              borderRadius: 12, fontSize: 20, fontWeight: 700,
              fontFamily: 'inherit', outline: 'none',
              color: colors.text, background: isInvalid ? '#fff5f5' : '#fff',
            }}
          />
          {isInvalid && (
            <div
              style={{
                marginTop: 8,
                display: 'flex', alignItems: 'center', gap: 6,
                fontSize: 12, fontWeight: 600, color: '#dc2626',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="#dc2626" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              Marks cannot be more than {subject.maxMarks} (max marks)
            </div>
          )}
        </div>

        {/* Footer buttons */}
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: '15px', background: '#fff', color: colors.text,
              border: `1.5px solid ${colors.border}`, borderRadius: 14,
              fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isInvalid}
            style={{
              flex: 1, padding: '15px', border: 'none', borderRadius: 14,
              background: isInvalid ? colors.primaryLight : colors.primaryDark,
              fontSize: 15, fontWeight: 700, cursor: isInvalid ? 'not-allowed' : 'pointer',
              color: '#fff', fontFamily: 'inherit',
            }}
          >
            Save marks
          </button>
        </div>
      </div>
    </>
  );
};

export default MarksEntrySheet;
